import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGetOptionCategoriesQuery } from '../../../features/details/detailsApiSlice';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { CATEGORY_SELECT_STYLES, DropdownIndicator, Option, ValueContainer } from '../../../animations/selectDetails';
import { selectCurrentLanguage } from '../../../features/auth/authSlice';
import { useSelector } from 'react-redux';
import { fireCategoriesTr } from '../../../utils/firebase.config';

const CategoryMultiSelect = ({ selectedCategories = [], setCategories, categoryCounts = [] }) => {
    const { t } = useTranslation();
    const lang = useSelector(selectCurrentLanguage);
    const [translations, setTranslations] = useState(null);
    const retryCount = useRef(0);
    const {
        data: optionCategories = [],
        isLoading,
        isError,
        isFetching,
        refetch
    } = useGetOptionCategoriesQuery(undefined, { refetchOnMountOrArgChange: true });

    useEffect(() => {
        fireCategoriesTr(setTranslations).catch(error => {
            console.warn('Category translations are unavailable; using source names.', error);
        });
    }, []);

    useEffect(() => {
        if (!isError || retryCount.current >= 2) return undefined;
        retryCount.current += 1;
        const timer = window.setTimeout(refetch, 800 * retryCount.current);
        return () => window.clearTimeout(timer);
    }, [isError, refetch]);

    const countById = useMemo(() => new Map(
        categoryCounts.map(item => [Number(item.id), Number(item.count) || 0])
    ), [categoryCounts]);

    const options = useMemo(() => optionCategories
        .filter(item => item.category !== 'Some category')
        .map(item => {
            const index = translations?.input?.indexOf(item.category);
            const translated = translations?.translated?.[index]?.[lang] || item.category;
            const count = countById.has(Number(item.id)) ? countById.get(Number(item.id)) : null;
            return {
                value: Number(item.id),
                rawLabel: translated,
                label: count === null ? translated : `${translated} (${count})`,
                count
            };
        })
        .sort((a, b) => {
            const aEmpty = a.count === 0;
            const bEmpty = b.count === 0;
            if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
            return a.rawLabel.localeCompare(b.rawLabel);
        }), [optionCategories, translations, lang, countById]);

    const selectedOptions = useMemo(() => options.filter(option =>
        selectedCategories.map(Number).includes(option.value)
    ), [options, selectedCategories]);

    const handleChange = values => {
        setCategories((values || []).map(option => option.value));
    };

    const selectedCount = selectedOptions.length;
    const placeholder = selectedCount === 0
        ? t('SelectACategory')
        : selectedCount === 1
            ? t('oneItemSelected', { count: selectedCount })
            : t('itemsSelected', { count: selectedCount });

    if (isError && retryCount.current >= 2) {
        return <div style={{ color: '#b42318', fontSize: '14px' }}>
            <div>{t('CategoriesLoadError')}</div>
            <button type="button" className="button margin-top-10" onClick={() => {
                retryCount.current = 0;
                refetch();
            }}>{t('Retry')}</button>
        </div>;
    }

    return <Select
        components={{ DropdownIndicator, ValueContainer, Option }}
        isMulti
        value={selectedOptions}
        onChange={handleChange}
        closeMenuOnSelect={false}
        placeholder={placeholder}
        isClearable={false}
        hideSelectedOptions={false}
        styles={CATEGORY_SELECT_STYLES}
        options={options}
        isDisabled={isLoading || isFetching}
        isLoading={isLoading || isFetching}
        isOptionDisabled={option => option.count === 0}
        noOptionsMessage={() => t('NoCategoriesAvailable')}
    />;
};

export default CategoryMultiSelect;
